import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ImageIcon,
  FileText,
  File,
  Search,
  Upload,
  Trash2,
  Copy,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/Admin";
import Pagination from "@/components/admin/pagination";
import Loader from "@/components/loader/loader";

export default function AdminLibraryPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const { getFiles, deleteFiles } = useAdminAuth();
  const [files, setFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch files from backend
  const fetchFiles = async () => {
    setIsLoading(true);
    try {
      const result = await getFiles(currentPage, rowsPerPage);
      const normalized = result.files.map((file) => ({
        id: file.id,
        name: file.name,
        url: file.url,
        type: file.type,
        createdAt: file.createdAt,
        size: file.size,
      }));
      setFiles(normalized);
      setTotalFiles(result.pagination.total);
      setTotalPages(result.pagination.totalPages);
    } catch (err) {
      toast.error("Failed to load files.");
      console.error(err);
    } finally {
      setIsLoading(false); // stop loading
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [currentPage, rowsPerPage]);

  // Clear selection when page changes
  useEffect(() => {
    setSelectedFiles(new Set());
  }, [currentPage]);

  // Filter files by search term (client-side filtering on current page)
  const filteredFiles = files.filter((file) =>
    (file?.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Use filtered files for display (or all files if no search)
  const displayFiles = searchTerm ? filteredFiles : files;

  const getFileIcon = (type) => {
    switch (type) {
      case "image":
        return <ImageIcon className="h-8 w-8 text-muted-foreground" />;
      case "document":
        return <FileText className="h-8 w-8 text-muted-foreground" />;
      case "spreadsheet":
      case "video":
      default:
        return <File className="h-8 w-8 text-muted-foreground" />;
    }
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const handleCopyUrl = () => {
    if (selectedFile?.url) {
      navigator.clipboard.writeText(selectedFile.url);
      toast.success("URL Copied");
    }
  };

  const handleDeleteFile = async () => {
    if (!selectedFile) return;
    try {
      await deleteFiles([selectedFile.id]);
      setIsModalOpen(false);
      setSelectedFile(null);
      setSelectedFiles(new Set());
      fetchFiles();
    } catch {
      // Notification handled in context
    }
  };

  const handleSelectFile = (fileId) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(displayFiles.map((file) => file.id));
      setSelectedFiles(allIds);
    } else {
      setSelectedFiles(new Set());
    }
  };

  const isAllSelected = displayFiles.length > 0 && displayFiles.every((file) => selectedFiles.has(file.id));
  const isSomeSelected = displayFiles.some((file) => selectedFiles.has(file.id));
  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isSomeSelected && !isAllSelected;
    }
  }, [isSomeSelected, isAllSelected]);

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) return;
    
    setIsDeleting(true);
    try {
      await deleteFiles(Array.from(selectedFiles));
      setSelectedFiles(new Set());
      setIsDeleteDialogOpen(false);
      fetchFiles();
    } catch {
      // Notification handled in context
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Media Library</h1>
        <div className="flex items-center gap-2">
          {selectedFiles.size > 0 && (
            <Button
              variant="destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete {selectedFiles.size} {selectedFiles.size === 1 ? "File" : "Files"}
            </Button>
          )}
          <Link to="/admin/library/upload">
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              Upload File
            </Button>
          </Link>
        </div>
      </div>

      {/* Search and Total Count */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search files..."
            className="w-full rounded-lg bg-background pl-8 md:w-[300px] lg:w-[450px]"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          {searchTerm ? (
            <>
              Showing {filteredFiles.length} of {totalFiles} file{totalFiles !== 1 ? "s" : ""}
            </>
          ) : (
            <>
              Total: {totalFiles} file{totalFiles !== 1 ? "s" : ""}
            </>
          )}
        </div>
      </div>

      {/* File Grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {isLoading ? (
          <div className="col-span-full flex justify-center py-10">
            <Loader />
          </div>
        ) : displayFiles.length > 0 ? (
          <>
            {/* Select All Checkbox */}
            <div className="col-span-full flex items-center gap-2 pb-2">
              <Checkbox
                ref={selectAllCheckboxRef}
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm text-muted-foreground">
                {selectedFiles.size > 0
                  ? `${selectedFiles.size} file${selectedFiles.size === 1 ? "" : "s"} selected`
                  : "Select all"}
              </span>
            </div>
            {displayFiles.map((file) => (
              <div
                key={file.id}
                className="group relative flex cursor-pointer flex-col overflow-hidden rounded-lg border bg-transparent shadow-none transition hover:shadow-none"
              >
                <div
                  className="relative aspect-square flex items-center justify-center"
                  onClick={() => handleFileClick(file)}
                >
                  {file.type === "image" && file.url ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      {getFileIcon(file.type)}
                    </div>
                  )}
                  <Badge
                    variant="secondary"
                    className="absolute bottom-2 right-2 text-xs capitalize"
                  >
                    {file.type}
                  </Badge>
                  <div
                    className="absolute top-2 left-2 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectFile(file.id);
                    }}
                  >
                    <Checkbox
                      checked={selectedFiles.has(file.id)}
                      onCheckedChange={() => handleSelectFile(file.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <div className="flex flex-col p-2">
                  <span className="truncate text-sm font-medium">
                    {file.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {file.size}
                  </span>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="col-span-full text-center text-muted-foreground">
            No files found.
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && !searchTerm && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      )}

      {/* File Modal */}
      {selectedFile && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{selectedFile.name}</DialogTitle>
              <DialogDescription>
                View details and manage this file.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="flex items-center justify-center rounded-md p-4">
                {selectedFile.type === "image" ? (
                  <img
                    src={selectedFile.url || "/placeholder.svg"}
                    alt={selectedFile.name}
                    className="max-h-48 w-auto object-contain"
                  />
                ) : (
                  getFileIcon(selectedFile.type)
                )}
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Filename</Label>
                <Input
                  value={selectedFile.name}
                  readOnly
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Uploaded At</Label>
                <Input
                  value={selectedFile.createdAt || ""}
                  readOnly
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">URL</Label>
                <div className="col-span-3 flex items-center space-x-2">
                  <Input value={selectedFile.url} readOnly className="flex-1" />
                  <Button variant="outline" size="icon" onClick={handleCopyUrl}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter className="flex flex-col sm:flex-row sm:justify-between">
              <Button variant="destructive" onClick={handleDeleteFile}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete File
              </Button>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Files</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedFiles.size} file{selectedFiles.size === 1 ? "" : "s"}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
